// Iter 119 — ForgedOps Ask V1 (verification engine, NOT a chatbot).
//
// Hits POST /api/ask, renders:
//   - the verified answer (or the explicit "No verified answer found." refusal)
//   - the spec sections that grounded the answer (clickable to /spec-section/:id)
//   - the source PDF deep-link (#page=N) so the user can confirm visually
//   - the related sheets / findings / RFIs / submittals / contracts / CORE
//
// Hard rules enforced by the engine — the UI only renders what the backend
// returns. We never inject suggestions or fallback prose.
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, ShieldCheck, ShieldAlert, ShieldX, FileText, Layers, Lightbulb,
  Inbox, ExternalLink, Loader2, ChevronRight, MessageSquareText,
  Save, Share2, Download, FilePlus, MessageCircle, Archive,
  RotateCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, API_BASE, getBearer } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTED = [
  "What governs limerock base?",
  "What governs superpave asphalt?",
  "What governs thermoplastic striping?",
  "What governs performance turf?",
  "What governs MOT?",
  "What governs pipe culverts?",
  "What governs inlets?",
  "What governs manholes?",
];

function ConfidencePill({ value }) {
  const v = (value || "").toUpperCase();
  const map = {
    HIGH:   { color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: ShieldCheck, label: "Verified · High confidence" },
    MEDIUM: { color: "bg-amber-500/15  text-amber-300  border-amber-500/30",  icon: ShieldCheck, label: "Verified · Medium confidence" },
    LOW:    { color: "bg-orange-500/15 text-orange-300 border-orange-500/30", icon: ShieldAlert, label: "Verified · Low confidence" },
    NONE:   { color: "bg-red-500/15    text-red-300    border-red-500/30",    icon: ShieldAlert, label: "No verified source" },
    // FP-081 (2026-09-09 audit remediation) — a real, distinct state:
    // the model cited a genuine source, but an independent support
    // check found the cited text does NOT actually support the
    // specific claim. This must NEVER render as "Verified" — citing a
    // real source ID is not the same as that source confirming the
    // claim.
    UNSUPPORTED: { color: "bg-red-600/20 text-red-400 border-red-600/40", icon: ShieldX, label: "NOT verified · cited source does not confirm this" },
  };
  const cfg = map[v] || map.NONE;
  const Icon = cfg.icon;
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-mono uppercase tracking-wider ${cfg.color}`} data-testid="ask-confidence">
      <Icon size={14} />
      {cfg.label}
    </div>
  );
}

function SectionHeader({ icon: Icon, label, count }) {
  if (!count) return null;
  return (
    <div className="flex items-center gap-2 mt-6 mb-2 px-1">
      <Icon size={14} className="text-[var(--fop-text-3)]" />
      <h3 className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
        {label} <span className="text-[var(--fop-text-4)]">({count})</span>
      </h3>
    </div>
  );
}

function SourceCard({ source }) {
  return (
    <div className="rounded-lg border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] p-4" data-testid="ask-source-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="font-mono text-[11px] bg-[var(--fop-surface-3)] border-[var(--fop-border-2)]" data-testid={`ask-source-number-${source.section_number}`}>
              Section {source.section_number}
            </Badge>
            {source.reference_count > 0 && (
              <span className="text-[11px] font-mono text-[var(--fop-text-4)]">
                {source.reference_count} reference{source.reference_count !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="text-sm font-semibold text-[var(--fop-text)] mb-0.5">{source.section_title || "—"}</div>
          {(source.division_number || source.division_title) && (
            <div className="text-xs text-[var(--fop-text-3)]">
              Division {source.division_number || "—"} {source.division_title || ""}
            </div>
          )}
          {(source.page_start || source.page_end) && (
            <div className="text-xs text-[var(--fop-text-4)] mt-0.5">
              Pages {source.page_start ?? "—"}–{source.page_end ?? "—"}
            </div>
          )}
          {source.source_document && (
            <div className="text-xs text-[var(--fop-text-4)] mt-0.5 truncate" title={source.source_document}>
              {source.source_document}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Link to={`/spec-section/${source.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-[var(--fop-primary)] hover:underline" data-testid={`ask-open-spec-${source.section_number}`}>
          <Layers size={13} /> Open spec section
        </Link>
        {source.source_pdf_url && (
          <a href={source.source_pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-[var(--fop-primary)] hover:underline" data-testid={`ask-open-pdf-${source.section_number}`}>
            <ExternalLink size={13} /> Open source PDF · page {source.page_start || 1}
          </a>
        )}
      </div>
    </div>
  );
}

function RelatedList({ items, render, testid }) {
  if (!items || !items.length) return null;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {items.map(render)}
    </div>
  );
}

export default function AskPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  // Iter 120 — operationalization state
  const [savedRecord, setSavedRecord] = useState(null);  // knowledge record once saved
  const [savingRecord, setSavingRecord] = useState(false);
  const [converting, setConverting] = useState(null);     // 'finding' | 'rfi' | 'submittal' | 'share'
  const [shareLink, setShareLink] = useState(null);

  const ask = async (question, { bypass = false } = {}) => {
    const cleaned = (question || q).trim();
    if (cleaned.length < 2) return;
    setLoading(true);
    setErr(null);
    setResult(null);
    setSavedRecord(null);
    setShareLink(null);
    try {
      const client_op_id = (window.crypto?.randomUUID?.() || `ask_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      const r = await api.post("/ask", { question: cleaned, bypass_reuse: bypass, client_op_id }, { timeout: 90000 });
      setResult(r.data);
      // Iter 120 P7 — if the engine returned a cached record, pre-fill `savedRecord`
      // so the user can immediately Share / Export / Create Finding without re-saving.
      if (r.data?.reused && r.data?.reused_record_id) {
        try {
          const det = await api.get(`/knowledge-records/${r.data.reused_record_id}`);
          setSavedRecord(det.data);
        } catch { /* non-fatal */ }
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Verification engine error");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); ask(); };
  const onSuggested = (s) => { setQ(s); ask(s); };

  const onSaveRecord = async () => {
    if (!result || !result.verified) return;
    setSavingRecord(true);
    try {
      const r = await api.post("/knowledge-records", {
        question: result.question,
        answer: result.answer,
        confidence: result.confidence,
        sources: result.sources,
        citations: result.citations,
        related: result.related,
        ask_session_id: result.ask_session_id,
      });
      setSavedRecord(r.data);
      toast.success("Knowledge record created — every citation is now permanent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create knowledge record.");
    } finally {
      setSavingRecord(false);
    }
  };

  const onConvert = async (kind) => {
    if (!savedRecord) return;
    setConverting(kind);
    try {
      const endpoint = {
        finding: `/knowledge-records/${savedRecord.id}/create-finding`,
        rfi: `/knowledge-records/${savedRecord.id}/create-rfi`,
        submittal: `/knowledge-records/${savedRecord.id}/create-submittal`,
      }[kind];
      const payload = (kind === "finding")
        ? { priority: "medium" }
        : { project_id: savedRecord.project_id || result?.project_id || null };
      const r = await api.post(endpoint, payload);
      if (kind === "finding") {
        toast.success("Field Finding created with full traceability.");
      } else if (kind === "rfi") {
        toast.success(`RFI draft ${r.data?.number} created.`);
      } else {
        toast.success(`Submittal draft ${r.data?.number} created.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not create ${kind} draft.`);
    } finally {
      setConverting(null);
    }
  };

  const onShare = async () => {
    if (!savedRecord) return;
    setConverting("share");
    try {
      const r = await api.post(`/knowledge-records/${savedRecord.id}/share`, {
        expires_in_hours: 24 * 30,
      });
      const url = `${window.location.origin}/k-share/${r.data.token}`;
      setShareLink(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied. Expires in 30 days.");
      } catch {
        toast.success("Share link ready. Copy from the input below.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link.");
    } finally {
      setConverting(null);
    }
  };

  const onExportPDF = () => {
    if (!savedRecord) return;
    // Use a temporary anchor with the bearer token in the query string is NOT
    // safe; instead use fetch with Authorization header and trigger blob download.
    (async () => {
      try {
        const tok = getBearer();
        const resp = await fetch(`${API_BASE}/knowledge-records/${savedRecord.id}/export.pdf`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (!resp.ok) throw new Error(`PDF ${resp.status}`);
        const blob = await resp.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `knowledge-${savedRecord.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("PDF downloaded.");
      } catch (e) {
        toast.error("Could not export PDF.");
      }
    })();
  };

  const refused = result && result.verified === false;
  const verified = result && result.verified === true;
  const reused = result && result.reused === true;

  return (
    <div className="min-h-screen bg-[var(--fop-surface)] text-[var(--fop-text)]">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquareText size={18} className="text-[var(--fop-primary)]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">
              ForgedOps Ask · Verified knowledge engine
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Ask a construction question</h1>
          <p className="text-[var(--fop-text-3)] text-sm sm:text-base leading-relaxed max-w-2xl">
            Every answer is grounded in your project&apos;s specifications. If we can&apos;t show you the source, we won&apos;t answer. No guesses, no AI opinions, no fabricated citations.
          </p>
        </div>

        {/* Search box */}
        <form onSubmit={onSubmit} className="mb-6">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--fop-text-4)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g., What governs limerock base?"
              data-testid="ask-input"
              autoFocus
              className="w-full pl-12 pr-32 py-4 rounded-xl border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] text-[var(--fop-text)] placeholder:text-[var(--fop-text-4)] focus:outline-none focus:border-[var(--fop-primary)] focus:ring-2 focus:ring-[var(--fop-primary)]/20 text-base"
            />
            <Button
              type="submit"
              disabled={loading || q.trim().length < 2}
              data-testid="ask-submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-10"
            >
              {loading ? <><Loader2 size={14} className="animate-spin mr-2" /> Searching…</> : <>Ask <ChevronRight size={14} className="ml-1" /></>}
            </Button>
          </div>
        </form>

        {/* Suggested questions */}
        {!result && !loading && (
          <div className="mb-8">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-4)] mb-3">Try one of these</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  onClick={() => onSuggested(s)}
                  data-testid={`ask-suggested-${s.slice(0, 24).replace(/\s+/g, "-")}`}
                  className="px-3 py-1.5 text-xs rounded-full border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] text-[var(--fop-text-2)] hover:border-[var(--fop-primary)] hover:text-[var(--fop-text)] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="ask-error">
            {err}
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-3" data-testid="ask-loading">
            <div className="h-6 bg-[var(--fop-surface-2)] rounded animate-pulse w-1/3" />
            <div className="h-24 bg-[var(--fop-surface-2)] rounded animate-pulse" />
            <div className="h-32 bg-[var(--fop-surface-2)] rounded animate-pulse" />
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-5" data-testid="ask-result">
            {/* Refusal */}
            {refused && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6" data-testid="ask-refusal">
                <div className="flex items-start gap-3">
                  <ShieldAlert size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-semibold text-base mb-1.5">No verified answer found.</div>
                    <div className="text-sm text-[var(--fop-text-3)] mb-3">{result.reason || "The available sources do not directly answer this question."}</div>
                    <div className="text-xs text-[var(--fop-text-4)] leading-relaxed">
                      ForgedOps Ask never guesses. If the answer isn&apos;t in your project&apos;s specifications, sheets, findings, or intake documents, we will not invent one. Add the missing document or rephrase the question with a section number or topic that&apos;s already in the library.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Verified answer */}
            {verified && (
              <>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <ConfidencePill value={result.confidence} />
                  <div className="text-xs font-mono text-[var(--fop-text-4)]">
                    {(result.sources || []).length} spec source{(result.sources || []).length !== 1 ? "s" : ""} · {(result.citations || []).length} citation{(result.citations || []).length !== 1 ? "s" : ""}
                  </div>
                </div>

                {/* Iter 124 — Construction Language synonym chips. When
                    the deterministic synonym layer expanded the query
                    (e.g. "earthwork" → "excavation/embankment"), surface
                    the chain here so the field user sees WHY the answer
                    matched. The data comes verbatim from the API
                    (`synonym_expansions[]`), no client-side guessing. */}
                {(result.synonym_expansions || []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="ask-synonym-chips">
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-4)] mr-1">
                      Matched via construction language:
                    </span>
                    {result.synonym_expansions.map((ex, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] text-[11px] font-mono text-[var(--fop-text-2)]"
                        title={`expanded to: ${(ex.expanded_to || []).join(", ")}`}
                        data-testid={`ask-synonym-${(ex.matched || "").replace(/\s+/g, "-")}`}
                      >
                        {ex.matched} → {(ex.expanded_to || []).slice(0, 2).join(" · ")}
                        {(ex.expanded_to || []).length > 2 ? "…" : ""}
                      </span>
                    ))}
                  </div>
                )}

                {/* Iter 120 P7 — Reused-record banner */}
                {reused && result.reused_record_id && (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-start gap-3" data-testid="ask-reused-banner">
                    <Archive size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div className="text-xs leading-relaxed flex-1 min-w-0">
                      <span className="font-medium text-emerald-300">Previously verified answer</span>
                      <span className="text-[var(--fop-text-3)]"> — saved {result.reused_record_created_at?.slice(0,10)} by {result.reused_record_created_by || "—"}. No new AI call was made; this is the trusted record.</span>
                      <Link to={`/knowledge/${result.reused_record_id}`} className="ml-2 text-[var(--fop-primary)] hover:underline">Open record</Link>
                    </div>
                    <button
                      onClick={() => ask(result.question, { bypass: true })}
                      data-testid="ask-rerun-fresh"
                      className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)] flex items-center gap-1"
                    >
                      <RotateCw size={12} /> Re-verify
                    </button>
                  </div>
                )}

                <div className="rounded-xl border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] p-5 sm:p-6" data-testid="ask-answer">
                  <div className="text-base sm:text-lg leading-relaxed text-[var(--fop-text)] whitespace-pre-wrap">
                    {result.answer}
                  </div>
                </div>

                {/* Iter 120 — Operationalization action bar */}
                <div className="rounded-xl border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)]/50 p-4" data-testid="ask-action-bar">
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-3">Make this answer permanent</div>
                  <div className="flex flex-wrap gap-2">
                    {!savedRecord ? (
                      <Button
                        onClick={onSaveRecord}
                        disabled={savingRecord}
                        data-testid="ask-save-record"
                        className="bg-[var(--fop-primary)] hover:bg-[var(--fop-primary)]/90"
                      >
                        {savingRecord ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                        Create Project Knowledge Record
                      </Button>
                    ) : (
                      <>
                        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 px-2.5 py-1 font-mono text-[11px]" data-testid="ask-record-saved-pill">
                          <Save size={11} className="mr-1" /> Saved · {savedRecord.id.slice(0,12)}…
                        </Badge>
                        <Button variant="outline" size="sm" onClick={() => onConvert("finding")} disabled={!!converting} data-testid="ask-create-finding">
                          {converting === "finding" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Lightbulb size={12} className="mr-1.5" />} Create Field Finding
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => onConvert("rfi")} disabled={!!converting} data-testid="ask-create-rfi">
                          {converting === "rfi" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <MessageCircle size={12} className="mr-1.5" />} Create RFI Draft
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => onConvert("submittal")} disabled={!!converting} data-testid="ask-create-submittal">
                          {converting === "submittal" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <FilePlus size={12} className="mr-1.5" />} Create Submittal Draft
                        </Button>
                        <Button variant="outline" size="sm" onClick={onShare} disabled={!!converting} data-testid="ask-share">
                          {converting === "share" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Share2 size={12} className="mr-1.5" />} Share
                        </Button>
                        <Button variant="outline" size="sm" onClick={onExportPDF} disabled={!!converting} data-testid="ask-export-pdf">
                          <Download size={12} className="mr-1.5" /> Export PDF
                        </Button>
                        <Link to={`/knowledge/${savedRecord.id}`} className="inline-flex items-center text-xs font-medium text-[var(--fop-primary)] hover:underline px-2 py-1" data-testid="ask-open-record">
                          Open record <ChevronRight size={12} />
                        </Link>
                      </>
                    )}
                  </div>
                  {shareLink && (
                    <div className="mt-3 flex items-center gap-2" data-testid="ask-share-link-row">
                      <input
                        readOnly
                        value={shareLink}
                        className="flex-1 text-xs font-mono px-3 py-2 rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface)] text-[var(--fop-text-2)]"
                        onFocus={(e) => e.target.select()}
                        data-testid="ask-share-link-input"
                      />
                      <a href={shareLink} target="_blank" rel="noreferrer" className="text-xs text-[var(--fop-primary)] hover:underline">
                        Open <ExternalLink size={11} className="inline" />
                      </a>
                    </div>
                  )}
                </div>

                {/* Citations */}
                {(result.citations || []).length > 0 && (
                  <div className="rounded-lg border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)]/50 p-4" data-testid="ask-citations">
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-3">Why we trust this answer</div>
                    <ul className="space-y-2">
                      {(result.citations || []).map((c, i) => (
                        <li key={i} className="text-sm leading-relaxed">
                          <Link to={`/spec-section/${c.spec_id}`} className="font-mono text-[var(--fop-primary)] hover:underline mr-2">
                            §{c.section_number}
                          </Link>
                          <span className="text-[var(--fop-text-2)]">{c.section_title}</span>
                          {c.reason && <div className="text-xs text-[var(--fop-text-3)] mt-0.5 ml-7">{c.reason}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Source specifications */}
                <SectionHeader icon={FileText} label="Source specifications" count={(result.sources || []).length} />
                <div className="grid gap-3 sm:grid-cols-2">
                  {(result.sources || []).map((s) => (<SourceCard key={s.id} source={s} />))}
                </div>

                {/* Related sheets */}
                <SectionHeader icon={Layers} label="Related sheets" count={(result.related?.sheets || []).length} />
                <RelatedList
                  testid="ask-related-sheets"
                  items={result.related?.sheets || []}
                  render={(s) => (
                    <Link key={s.id} to={`/sheet/${s.id}`} className="flex items-center justify-between gap-3 rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] px-3 py-2 hover:border-[var(--fop-primary)]">
                      <div className="min-w-0">
                        <span className="font-mono text-sm text-[var(--fop-primary)] mr-2">{s.sheet_number}</span>
                        <span className="text-sm text-[var(--fop-text-2)]">{s.sheet_title || "—"}</span>
                      </div>
                      <ChevronRight size={14} className="text-[var(--fop-text-4)] flex-shrink-0" />
                    </Link>
                  )}
                />

                {/* Related findings */}
                <SectionHeader icon={Lightbulb} label="Related field findings" count={(result.related?.findings || []).length} />
                <RelatedList
                  testid="ask-related-findings"
                  items={result.related?.findings || []}
                  render={(f) => (
                    <div key={f.id} className="rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] px-3 py-2">
                      <div className="text-sm font-medium text-[var(--fop-text)]">{f.title || "(untitled finding)"}</div>
                      <div className="text-xs text-[var(--fop-text-3)] mt-0.5">
                        {f.sheet_number && <span className="font-mono mr-2">Sheet {f.sheet_number}</span>}
                        {f.status && <Badge variant="outline" className="text-[10px] mr-1">{f.status}</Badge>}
                        {f.priority && <Badge variant="outline" className="text-[10px]">{f.priority}</Badge>}
                      </div>
                    </div>
                  )}
                />

                {/* Related intake documents (RFIs / submittals / contracts / other) */}
                {["rfis", "submittals", "contracts", "intake_documents", "core_references"].map((k) => {
                  const label = {
                    rfis: "Related RFIs",
                    submittals: "Related submittals",
                    contracts: "Related contract documents",
                    intake_documents: "Related intake documents",
                    core_references: "Related CORE records",
                  }[k];
                  const items = result.related?.[k] || [];
                  return (
                    <React.Fragment key={k}>
                      <SectionHeader icon={Inbox} label={label} count={items.length} />
                      <RelatedList
                        testid={`ask-related-${k}`}
                        items={items}
                        render={(d) => (
                          <div key={d.id} className="rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] px-3 py-2">
                            <div className="text-sm text-[var(--fop-text-2)] truncate" title={d.file_name || d.title || d.id}>
                              {d.file_name || d.title || d.id}
                            </div>
                            <div className="text-xs text-[var(--fop-text-4)] mt-0.5">
                              {d.intake_category || d.core_record_type || ""}
                            </div>
                          </div>
                        )}
                      />
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
